/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import { useParams } from 'react-router-dom';

import { useFirstPrinciplesChat } from './first-principles.hooks';

import { FpTopBar, FpNavRow } from './header/header';
import { FpWelcomePage } from './welcome-page';
import { FpConversation } from './conversations/first-principles-conversation';
import { FpReview } from './review/review';
import { AnimatedDockContainer } from '../onboarding/dock-animations/AnimatedDockContainer';

import { PageRoot, PageInner, CenterStack, DockWrapper, VSpace } from './first-principles.styled';
import { readFpCookie } from './utils/fp-cookie';

type SlideDirection = 1 | -1;

function detectSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /Safari/i.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|EdgiOS|Android/i.test(ua);
}

function upsertMeta(name: string, content: string) {
  if (typeof document === 'undefined') return;
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function upsertLink(rel: string, href: string, extra?: Record<string, string>) {
  if (typeof document === 'undefined') return;
  const key = `${rel}:${href}`;
  let link = document.querySelector(`link[data-upsert="${key}"]`) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    link.setAttribute('href', href);
    link.setAttribute('data-upsert', key);
    if (extra) {
      for (const [k, v] of Object.entries(extra)) link.setAttribute(k, v);
    }
    document.head.appendChild(link);
  }
}

function preloadImages(urls: string[]) {
  if (typeof window === 'undefined') return;
  const seen = new Set<string>();
  urls.forEach((src) => {
    const s = String(src || '').trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    const img = new Image();
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = s;
  });
}

export const FirstPrinciples: React.FC = () => {
  const fp = useFirstPrinciplesChat();

  const { nickname: placeholder } = useParams();
  const [direction, setDirection] = React.useState<SlideDirection>(1);
  const [dockResetTrigger, setDockResetTrigger] = React.useState(0);

  const current = fp.currentQuestion;
  const showDock = fp.isLoaded && !fp.showReview;

  const isSafari = React.useMemo(() => detectSafari(), []);

  const counterLabel = React.useMemo(() => {
    if (!fp.isLoaded) return '';
    if (fp.isLastQuestion) return 'End';
    return `${fp.currentIndex + 1}/${fp.questions.length}`;
  }, [fp.isLoaded, fp.isLastQuestion, fp.currentIndex, fp.questions.length]);

  const pageVariants: Variants = React.useMemo(() => {
    if (isSafari) {
      return {
        enter: (dir: SlideDirection) => ({
          x: dir === 1 ? 64 : -64,
          opacity: 0,
        }),
        center: {
          x: 0,
          opacity: 1,
          transition: {
            x: { type: 'spring' as const, stiffness: 520, damping: 42, mass: 0.9 },
            opacity: { duration: 0.18 },
          },
        },
        exit: (dir: SlideDirection) => ({
          x: dir === 1 ? -64 : 64,
          opacity: 0,
          transition: {
            x: { type: 'spring' as const, stiffness: 520, damping: 42, mass: 0.9 },
            opacity: { duration: 0.14 },
          },
        }),
      };
    }

    return {
      enter: (dir: SlideDirection) => ({
        x: dir === 1 ? 64 : -64,
        opacity: 0,
        filter: 'blur(6px)',
      }),
      center: {
        x: 0,
        opacity: 1,
        filter: 'blur(0px)',
        transition: {
          x: { type: 'spring' as const, stiffness: 520, damping: 42, mass: 0.9 },
          opacity: { duration: 0.18 },
          filter: { duration: 0.18 },
        },
      },
      exit: (dir: SlideDirection) => ({
        x: dir === 1 ? -64 : 64,
        opacity: 0,
        filter: 'blur(6px)',
        transition: {
          x: { type: 'spring' as const, stiffness: 520, damping: 42, mass: 0.9 },
          opacity: { duration: 0.14 },
          filter: { duration: 0.14 },
        },
      }),
    };
  }, [isSafari]);

  // Auto-load from cookie
  const autoLoadAttemptedRef = React.useRef(false);
  React.useEffect(() => {
    if (autoLoadAttemptedRef.current) return;
    if (fp.isLoaded) return;
    if (fp.loading) return;

    const c = readFpCookie();
    const code = c?.invitationCode?.trim();

    if (!code) {
      autoLoadAttemptedRef.current = true;
      return;
    }

    autoLoadAttemptedRef.current = true;

    fp.setInvitationCode(code);
    void fp.load(code);
  }, [fp]);

  const handleVerify = React.useCallback(
    async (code: string) => {
      await fp.load(code);
      setDirection(1);
    },
    [fp],
  );

  const goPrevAnimated = React.useCallback(async () => {
    if (!fp.isLoaded || !fp.canGoPrev) return;

    const prevIdx = Math.max(0, fp.currentIndex - 1);
    setDirection(-1);

    const res = await fp.updateProgress(prevIdx);
    if (res) fp.setCurrentIndex(prevIdx);
  }, [fp]);

  const handleReturnLater = React.useCallback(async () => {
    if (!fp.isLoaded) return;

    if (fp.isLastQuestion) {
      fp.setShowReview(true);
      return;
    }

    const nextIdx = fp.currentIndex + 1;
    setDirection(1);
    const res = await fp.updateProgress(nextIdx);
    if (res) fp.setCurrentIndex(nextIdx);
  }, [fp]);

  const handleTypeSubmit = React.useCallback(
    async (text: string) => {
      if (fp.submitting) return;

      if (fp.isLastQuestion) {
        await fp.submitCurrentTextAndProgress(text, fp.currentIndex);
        setDockResetTrigger((t) => t + 1);
        fp.setShowReview(true);
        return;
      }

      const nextIdx = fp.currentIndex + 1;
      const res = await fp.submitCurrentTextAndProgress(text, nextIdx);

      if (res) {
        setDirection(1);
        fp.setCurrentIndex(nextIdx);
        setDockResetTrigger((t) => t + 1);
      }
    },
    [fp],
  );

  const handleRecordSend = React.useCallback(
    async (audioBlob: Blob, _files?: File[], transcript?: string) => {
      if (fp.submitting) return;

      if (fp.isLastQuestion) {
        await fp.submitCurrentAudioAndProgress(audioBlob, transcript, fp.currentIndex);
        setDockResetTrigger((t) => t + 1);
        fp.setShowReview(true);
        return;
      }

      const nextIdx = fp.currentIndex + 1;
      const res = await fp.submitCurrentAudioAndProgress(audioBlob, transcript, nextIdx);

      if (res) {
        setDirection(1);
        fp.setCurrentIndex(nextIdx);
        setDockResetTrigger((t) => t + 1);
      }
    },
    [fp],
  );

  const handleSubmitReflections = React.useCallback(async () => {
    await fp.completeFlow();
    window.location.href = 'https://mailander.com';
  }, [fp]);

  const handleGoToQuestion = React.useCallback(
    (index: number) => {
      setDirection(-1);
      fp.goToQuestion(index);
    },
    [fp],
  );

  // Theme setup + preconnect + icon warmup
  React.useEffect(() => {
    const bg = '#1A1A1C';

    document.documentElement.style.background = bg;
    document.body.style.background = bg;

    (document.documentElement.style as any).overscrollBehavior = 'none';
    (document.body.style as any).overscrollBehavior = 'none';

    upsertMeta('theme-color', bg);
    upsertMeta('color-scheme', 'dark');

    upsertLink('dns-prefetch', '//mailander.com');
    upsertLink('preconnect', 'https://mailander.com', { crossorigin: '' });

    preloadImages([
      '/microphone.png',
      '/send.png',
      '/main-logo-white.svg',
      '/pause-with-bg.svg',
      '/icons/onboarding/question/resume.svg',
      '/cancel-voice.svg',
      '/icons/onboarding/question/pause.svg',
      '/icons/onboarding/question/listen.svg',
      '/icons/onboarding/question/stop-button.svg',
      '/icons/onboarding/question/send-button.svg',
      '/icons/onboarding/question/re-record.svg',
      '/icons/onboarding/question/file.svg',
      '/icons/onboarding/question/cancel-upload.svg',
      '/icons/onboarding/question/spinner.svg',
      '/chris.jpg',
      '/chris-signature.png',
    ]);
  }, []);

  return (
    <PageRoot>
      <PageInner>
        {/* ── Welcome (no header) ── */}
        {!fp.isLoaded ? (
          <CenterStack>
            <FpWelcomePage
              placeholder={placeholder}
              invitationCode={fp.invitationCode}
              setInvitationCode={fp.setInvitationCode}
              error={fp.error}
              loading={fp.loading}
              onVerify={handleVerify}
            />
          </CenterStack>
        ) : fp.showReview ? (
          /* ── Review ── */
          <>
          <FpTopBar counterLabel="End" />
          <FpReview
            questions={fp.questions}
            answers={fp.answers}
            submitting={fp.submitting}
            onGoToQuestion={handleGoToQuestion}
            onSubmit={handleSubmitReflections}
          />
          </>
        ) : (
          /* ── Question View ── */
          <>
            {/* Top bar: logo + counter + tagline */}
            <FpTopBar counterLabel={counterLabel} />

            {/* 24px desktop / 12px mobile gap */}
            <VSpace $h={24} $hMobile={12} />

            {/* Nav row: back + return later */}
            <FpNavRow
              counterLabel={counterLabel}
              canGoBack={fp.canGoPrev}
              onBack={goPrevAnimated}
              onReturnLater={handleReturnLater}
            />

            {/* 24px gap */}
            <VSpace $h={24} />

            {/* Chat content */}
            <main
              style={{
                width: '100%',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CenterStack>
                {current && (
                  <div style={{ width: '100%', flex: 1, minHeight: 0 }}>
                    {fp.error ? (
                      <div style={{ color: '#ffb4b4', textAlign: 'center', padding: 12 }}>
                        {fp.error}
                      </div>
                    ) : null}

                    <AnimatePresence mode="wait" initial={false} custom={direction}>
                      <motion.div
                        key={fp.currentIndex}
                        custom={direction}
                        variants={pageVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        style={{
                          width: '100%',
                          flex: 1,
                          minHeight: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                        }}
                      >
                        <FpConversation question={current} invitationCode={fp.invitationCode} />
                      </motion.div>
                    </AnimatePresence>
                  </div>
                )}
              </CenterStack>

              {showDock && (
                <DockWrapper>
                  <AnimatedDockContainer
                    defaultVariant="question"
                    onTypeSubmit={handleTypeSubmit}
                    onRecordSend={handleRecordSend}
                    typeInitialText=""
                    resetToDefault={dockResetTrigger > 0}
                    isSubmitting={fp.submitting}
                  />
                </DockWrapper>
              )}
            </main>
          </>
        )}
      </PageInner>
    </PageRoot>
  );
};
